import { consumableHeal, type ItemRefusalReason, type Rules } from "odyssey-shared";
import { GLYPH_SIZES } from "../../ui/kit/metrics.ts";
import type { GlyphName } from "../../ui/kit/Glyph.tsx";
import { say } from "../../words.ts";

/**
 * The Inventory window's wording (P2.8-T11, ADR-091 decision 21): the item tooltip, drafted from the rules tables so
 * no number is written into the page, a slot's accessible name, and the lines that name an item or a count. The
 * words are the language files' (`bag.*` in `odyssey-lang`). Pure: the page builds a tooltip only when one opens.
 * Every name and description is carried as text and drawn as text, never as markup.
 */

/**
 * The count inside the search field: the matching stacks over the stacks the bag holds, as the design writes it.
 *
 * @param hits The matching stacks.
 * @param stacks The stacks held.
 * @returns The count.
 * @example
 * foundCount(2, 7);
 * // "2/7"
 */
export function foundCount(hits: number, stacks: number): string {
  return `${hits}/${stacks}`;
}

/**
 * The item menu's heading (ADR-125 decision 7.1) and the item card's name line (decision 5.2): the item's name, and a
 * stack's count in brackets after it.
 *
 * @param name The item's name, as the rules name it.
 * @param quantity The stack's count.
 * @returns The heading.
 * @example
 * menuHeading("Minor Healing Potion", 5);
 * // "Minor Healing Potion (5)"
 */
export function menuHeading(name: string, quantity: number): string {
  return quantity > 1 ? say("bag.heading", { name, quantity: String(quantity) }) : name;
}

/**
 * The Destroy confirm's width in logical pixels, as Log out everywhere's is widened for its title, so the title's
 * display face fits whole (the kit's 208 cut its question mark).
 */
export const DESTROY_WIDTH = 256;

/**
 * The Drop confirm's width in logical pixels, the Destroy confirm's.
 */
export const DROP_WIDTH = 256;

/**
 * The rarities whose drop asks first: Rare and above (the author's ruling of 2026-10-10 for P2.8-T34).
 */
const ASKED_RARITIES: ReadonlySet<string> = new Set(["rare", "epic", "legendary", "relic"]);

/**
 * Whether dropping an item of a rarity asks first, by the item menu's Drop or a drag released over the world.
 *
 * @param rarity The item's rarity, as the rules name it.
 * @returns True for Rare, Epic, Legendary and Relic.
 * @example
 * dropAsksFirst("uncommon");
 * // false
 */
export function dropAsksFirst(rarity: string): boolean {
  return ASKED_RARITIES.has(rarity);
}

/**
 * How a drop goes (P2.8-T34): sent at once below Rare; for Rare and above, asked first where the page can open a confirm,
 * and refused, nothing sent, where it cannot, so a Rare item never drops unasked.
 *
 * @param rarity The item's rarity, as the rules name it.
 * @param canAsk Whether a confirm can open (the page has an input router).
 * @returns `send`, `ask` or `refuse`.
 * @example
 * dropHow("epic", false);
 * // "refuse"
 */
export function dropHow(rarity: string, canAsk: boolean): "send" | "ask" | "refuse" {
  return !dropAsksFirst(rarity) ? "send" : canAsk ? "ask" : "refuse";
}

/**
 * The Drop confirm's title: the item's name, with "x" and the count for a stack.
 *
 * @param name The item's name, as the rules name it.
 * @param count The stack's quantity.
 * @returns The title.
 * @example
 * dropTitle("Minor Healing Potion", 5);
 * // "Drop Minor Healing Potion x5?"
 */
export function dropTitle(name: string, count: number): string {
  return count > 1 ? say("bag.drop.titleCounted", { name, count: String(count) }) : say("bag.drop.title", { name });
}

/**
 * The Destroy confirm's body: what goes, and that it cannot be undone.
 *
 * @param name The item's name, as the rules name it.
 * @param count The stack's quantity.
 * @returns The sentence.
 * @example
 * destroyBody("Minor Healing Potion", 5);
 * // "5 Minor Healing Potion will be gone for good. This cannot be undone."
 */
export function destroyBody(name: string, count: number): string {
  return say("bag.destroy.body", { count: String(count), name });
}

/**
 * How much of its amount a consumable restores while its heal is halved (in combat with a player, ADR-091's
 * amendment of 2026-10-08), in percent, read from the rules' own `consumableHeal` so the window's note and mark and
 * the bar's line never state a number the server does not apply.
 */
export const COMBAT_PERCENT = consumableHeal(100, true);

/**
 * Whether an item's amounts are halved in combat with a player: its use restores health or stamina (ADR-091 decision 3
 * as amended 2026-10-08, and its note for P2.8-T57, items 2 and 7). A use that only cleanses or shortens is not, so
 * the bag's 50 mark goes on neither.
 *
 * @param rules The page's rules.
 * @param id The item's rules id.
 * @returns True for a consumable whose use restores something.
 * @example
 * halvedAgainstPlayers(rules, "minor_potion_of_cure_poison");
 * // false
 */
export function halvedAgainstPlayers(rules: Rules, id: string): boolean {
  const item = rules.items.get(id);
  const record = item === undefined || item.kind !== "consumable" || item.use === null ? undefined : rules.abilities.get(item.use);
  return record !== undefined && (record.heal !== null || record.restoreStamina !== null);
}

/**
 * The note the window shows under its grid while a consumable's heal is halved, in combat with a player: the bar's
 * line in the author's words (2026-10-08), as a sentence, the percent less read from the rules' own `consumableHeal`.
 *
 * @returns The sentence.
 * @example
 * combatNote();
 * // "When fighting players, potions restore 50% less."
 */
export function combatNote(): string {
  return `${barCombatLine()}.`;
}

/**
 * The message line's sentence for a use the server refused (P2.8-T12, ADR-091 decisions 10 and 13 and its amendment
 * of 2026-10-08), or null where the page says nothing. The author's ruling of 2026-10-08: a refused use is silent on
 * the page, since what is on screen already shows why (full health, the cooldown's wipe, the death screen, the stun,
 * the slot's count when its stack emptied or moved as the key was pressed), except a duel's refusal, "Not in a duel.",
 * which nothing else on screen explains. That one shows only when the duel rule refuses, a hook until P2.6-T1. The
 * refusal's sound is not this function's and plays for every reason.
 *
 * @param reason The reason the server gave.
 * @returns "Not in a duel." for `duel`, and null for every other reason.
 * @example
 * itemRefusalLine("cooldown");
 * // null
 */
export function itemRefusalLine(reason: ItemRefusalReason): string | null {
  return reason === "duel" ? say("bag.refused.duel") : null;
}

/**
 * The message line's sentence for a move the server refused (P2.8-T14, ADR-091 decision 21 and its note of that row),
 * or null where the page says nothing: every reason shows the design's sentence but `unavailable`, which the server
 * answers when it cannot say whether the move committed (another writer moved the bag, or the answer was lost) and
 * follows by sending the whole bag as it stands, so the slots themselves show what happened and "Nothing changed" could
 * be untrue.
 *
 * @param reason The reason the server gave.
 * @returns "The server refused that move. Nothing changed.", or null for `unavailable`.
 * @example
 * moveRefusalLine("busy");
 * // "The server refused that move. Nothing changed."
 */
export function moveRefusalLine(reason: ItemRefusalReason): string | null {
  return reason === "unavailable" ? null : say("bag.refused.move");
}

/**
 * The message line's sentence for a destroy the server refused (ADR-091's note for P2.8-T14 of the author's controls,
 * the recheck's finding 1), or null where the page says nothing: the server refuses a destroy whose slot no longer
 * holds the item in the count the player confirmed (loot merged onto it, a drink took from it), and the player is told
 * nothing went, since the stack they see may still be there with another count. `unavailable` says nothing, as for a
 * move: the bag read again shows what happened.
 *
 * @param reason The reason the server gave.
 * @returns "The stack changed. Nothing was destroyed.", or null for `unavailable`.
 * @example
 * destroyRefusalLine("empty");
 * // "The stack changed. Nothing was destroyed."
 */
export function destroyRefusalLine(reason: ItemRefusalReason): string | null {
  return reason === "unavailable" ? null : say("bag.refused.destroy");
}

/**
 * The message line's sentence for a drop the server refused (ADR-091's amendment for P2.8-T34, point 8), or null where
 * the page says nothing: `ground` while the ground has no free room, the stack staying in the bag; any other reason but
 * `unavailable`, whose bag is read again and sent whole, as a destroy's.
 *
 * @param reason The server's reason.
 * @returns The sentence, or null.
 * @example
 * dropRefusalLine("ground");
 * // "There is no room on the ground here."
 */
export function dropRefusalLine(reason: ItemRefusalReason): string | null {
  return reason === "unavailable" ? null : reason === "ground" ? say("bag.refused.dropGround") : say("bag.refused.dropChanged");
}

/**
 * The name a screen reader reads for the pending spinner under the grid while a move has waited
 * `PENDING_SPINNER_DELAY_MS` with no answer: the design's `inventory` card's `pending` note, "Moving the bow: waiting
 * for the server.", which the page no longer shows as text (P2.8-T41, ADR-091's note of that row).
 *
 * @param name The moving item's name, as the rules name it.
 * @returns The sentence.
 * @example
 * movingNote("Minor Healing Potion");
 * // "Moving the Minor Healing Potion: waiting for the server."
 */
export function movingNote(name: string): string {
  return say("bag.moving", { name });
}

/**
 * The line the bar's item slot adds to its tooltip while a consumable's heal is halved, in combat with a player (the
 * design's combat HUD card, `consumable` state, which draws it in the warning colour). The words are the author's
 * (2026-10-08, ADR-091's amendment of that day), with "restore" for "heal" since a potion may restore stamina (ADR-091's
 * note for P2.8-T57, the review's F4): "When fighting players, potions restore 50% less", the percent less read from
 * the rules' own `consumableHeal`.
 *
 * @returns The sentence.
 * @example
 * barCombatLine();
 * // "When fighting players, potions restore 50% less"
 */
export function barCombatLine(): string {
  return say("bag.barCombat", { percent: String(100 - COMBAT_PERCENT) });
}

/**
 * The size an item's glyph is drawn at inside a slot, in logical pixels (the design's `ItemSlot`: 16 inside 24).
 */
export const ITEM_GLYPH_SIZE = 16;

/**
 * An item's name as the rules give it, or "Unknown item" for an id the page's rules lack.
 *
 * @param rules The page's rules.
 * @param id The item's rules id.
 * @returns The name.
 * @example
 * itemName(rules, "minor_healing_potion");
 * // "Minor Healing Potion"
 */
export function itemName(rules: Rules, id: string): string {
  return rules.items.get(id)?.name ?? say("bag.unknownItem");
}

/**
 * A slot's accessible name: its item's name, with its count above one and "kept in place" (draft) for a stack kept in
 * place, which the slot's lock shows (ADR-125 decision 6.5); "Empty slot" for none; and for an id the page's rules
 * lack, which a server of the same rules never sends, "Unknown item".
 *
 * @param rules The page's rules.
 * @param id The item's rules id, or null for an empty slot.
 * @param quantity How many the slot holds.
 * @param kept Whether the stack is kept in place, false unless given.
 * @returns The name.
 * @example
 * slotLabel(rules, "minor_healing_potion", 20, true);
 * // "Minor Healing Potion, 20, kept in place"
 */
export function slotLabel(rules: Rules, id: string | null, quantity: number, kept = false): string {
  if (id === null) {
    return say("bag.emptySlot");
  }
  const name = itemName(rules, id);
  const counted = quantity > 1 ? say("bag.counted", { name, quantity: String(quantity) }) : name;
  return kept ? say("bag.keptSlot", { slot: counted, kept: { k: "bag.kept" } }) : counted;
}

/**
 * The glyph an item with no usable glyph of its own is drawn with, so that no slot is ever empty: a briefcase, 16 wide,
 * the slot's icon size.
 */
export const UNKNOWN_ITEM_GLYPH: GlyphName = "briefcase";

/**
 * The glyph an item is drawn with: the one its record names, when the page has it at the slot's icon size, else the
 * unknown-item glyph, so a thing with no art and no glyph still shows something. An item that names an icon and has no
 * usable glyph draws nothing until its art has decoded, so the unknown-item glyph never flashes before the art.
 *
 * @param rules The page's rules.
 * @param id The item's rules id.
 * @returns The glyph; `UNKNOWN_ITEM_GLYPH` for an item with no icon and no usable glyph (a glyph the page lacks or draws
 *   at another size) and for an id the rules lack; null for an item that names an icon and has no usable glyph.
 * @example
 * itemGlyph(rules, "minor_healing_potion");
 * // "drop"
 */
export function itemGlyph(rules: Rules, id: string): GlyphName | null {
  const item = rules.items.get(id);
  const glyph = item?.glyph ?? null;
  if (glyph !== null && Object.hasOwn(GLYPH_SIZES, glyph) && GLYPH_SIZES[glyph as GlyphName] === ITEM_GLYPH_SIZE) {
    return glyph as GlyphName;
  }
  return item?.icon === null || item === undefined ? UNKNOWN_ITEM_GLYPH : null;
}
